import { useState } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, Trash, Lock, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

const PROVIDER_META = {
    amazon: { label: "Amazon FR", emailLabel: "Email Amazon" },
    ovh: { label: "OVH Cloud", emailLabel: "Nic-handle ou email OVH" },
};

function ImapFields({ item, state, setState, onTestConnection, testing }) {
    return (
        <>
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2 col-span-2">
                    <Label htmlFor="divers-server">Serveur IMAP</Label>
                    <Input
                        id="divers-server"
                        placeholder="ex: imap.outlook.com / imap.gmail.com / ssl0.ovh.net"
                        value={state.imap_server}
                        onChange={(e) => setState((s) => ({ ...s, imap_server: e.target.value }))}
                        data-testid="provider-divers-server"
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="divers-port">Port</Label>
                    <Input
                        id="divers-port"
                        type="number"
                        value={state.imap_port}
                        onChange={(e) => setState((s) => ({ ...s, imap_port: e.target.value }))}
                        data-testid="provider-divers-port"
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="divers-ssl">SSL/TLS</Label>
                    <Select
                        value={state.imap_use_ssl ? "yes" : "no"}
                        onValueChange={(v) => setState((s) => ({ ...s, imap_use_ssl: v === "yes" }))}
                    >
                        <SelectTrigger id="divers-ssl" data-testid="provider-divers-ssl">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="yes">SSL (recommandé)</SelectItem>
                            <SelectItem value="no">Aucun</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <Label htmlFor="divers-folder">Dossier à scanner</Label>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onTestConnection}
                        disabled={testing}
                        data-testid="provider-divers-test"
                    >
                        {testing ? "Connexion…" : "Lister les dossiers"}
                    </Button>
                </div>
                {state.folders && state.folders.length > 0 ? (
                    <Select
                        value={state.folder}
                        onValueChange={(v) => setState((s) => ({ ...s, folder: v }))}
                    >
                        <SelectTrigger id="divers-folder" data-testid="provider-divers-folder">
                            <SelectValue placeholder="Choisis un dossier" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                            {state.folders.map((f) => (
                                <SelectItem key={f} value={f}>{f}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                ) : (
                    <Input
                        id="divers-folder"
                        placeholder="INBOX (clique sur 'Lister' pour découvrir tes dossiers)"
                        value={state.folder}
                        onChange={(e) => setState((s) => ({ ...s, folder: e.target.value }))}
                        data-testid="provider-divers-folder-input"
                    />
                )}
            </div>
        </>
    );
}

function ProviderRow({ item, onChanged }) {
    const meta = PROVIDER_META[item.provider] || { label: item.provider, emailLabel: "Identifiant" };
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState(item.email || "");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [imap, setImap] = useState({
        imap_server: item.imap_server || "",
        imap_port: item.imap_port || 993,
        imap_use_ssl: item.imap_use_ssl !== false,
        folder: item.folder || "INBOX",
        folders: [],
    });

    const isDivers = item.provider === "divers";

    const testConnection = async () => {
        if (!email || !password || !imap.imap_server) {
            toast.error("Saisis l'email, le mot de passe et le serveur IMAP d'abord");
            return;
        }
        setTesting(true);
        try {
            const { data } = await api.post("/divers/folders", {
                email, password,
                imap_server: imap.imap_server,
                imap_port: parseInt(imap.imap_port, 10) || 993,
                imap_use_ssl: imap.imap_use_ssl,
            });
            setImap((s) => ({ ...s, folders: data.folders || [] }));
            toast.success(`${(data.folders || []).length} dossiers trouvés`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Connexion échouée");
        } finally {
            setTesting(false);
        }
    };

    const save = async () => {
        if (!email || !password) {
            toast.error("Saisis identifiant et mot de passe");
            return;
        }
        if (isDivers && !imap.imap_server) {
            toast.error("Saisis le serveur IMAP");
            return;
        }
        setSaving(true);
        try {
            const payload = { email, password };
            if (isDivers) {
                payload.imap_server = imap.imap_server;
                payload.imap_port = parseInt(imap.imap_port, 10) || 993;
                payload.imap_use_ssl = imap.imap_use_ssl;
                payload.folder = imap.folder || "INBOX";
            }
            await api.put(`/credentials/${item.provider}`, payload);
            toast.success(`Identifiants ${meta.label} enregistrés (chiffrés)`);
            setPassword("");
            setOpen(false);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Supprimer les identifiants ${meta.label} ?`)) return;
        await api.delete(`/credentials/${item.provider}`);
        toast.success("Identifiants supprimés");
        onChanged?.();
    };

    return (
        <div className="border border-border rounded-md" data-testid={`provider-row-${item.provider}`}>
            <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="font-semibold">{meta.label}</div>
                    {item.configured ? (
                        <Badge variant="outline" className="text-emerald-700 border-emerald-200 text-xs">
                            <CheckCircle size={12} weight="fill" className="mr-1" />
                            Configuré
                        </Badge>
                    ) : (
                        <Badge variant="outline" className="text-muted-foreground text-xs">
                            Non configuré
                        </Badge>
                    )}
                    {item.email && (
                        <span className="text-xs text-muted-foreground truncate">{item.email}</span>
                    )}
                    {isDivers && item.folder && (
                        <span className="text-xs text-muted-foreground">→ {item.folder}</span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline" size="sm"
                        onClick={() => setOpen((v) => !v)}
                        data-testid={`provider-${item.provider}-edit`}
                    >
                        {item.configured ? "Modifier" : "Configurer"}
                    </Button>
                    {item.configured && (
                        <Button
                            variant="ghost" size="sm"
                            onClick={remove}
                            className="text-destructive hover:bg-destructive/10"
                            data-testid={`provider-${item.provider}-delete`}
                        >
                            <Trash size={14} />
                        </Button>
                    )}
                </div>
            </div>
            {open && (
                <div className="border-t border-border px-4 py-4 space-y-3 bg-muted/30">
                    <div className="space-y-2">
                        <Label htmlFor={`${item.provider}-email`}>{meta.emailLabel}</Label>
                        <Input
                            id={`${item.provider}-email`}
                            type="text"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid={`provider-${item.provider}-email`}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor={`${item.provider}-pwd`}>
                            {isDivers ? "Mot de passe d'application IMAP" : "Mot de passe"}
                        </Label>
                        <div className="relative">
                            <Input
                                id={`${item.provider}-pwd`}
                                type={show ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={item.configured ? "•••••••• (à ressaisir pour modifier)" : "Mot de passe"}
                                autoComplete="new-password"
                                data-testid={`provider-${item.provider}-password`}
                            />
                            <button
                                type="button"
                                onClick={() => setShow((v) => !v)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                            >
                                {show ? <EyeSlash size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                        {isDivers && (
                            <p className="text-xs text-muted-foreground">
                                Gmail/Outlook : génère un mot de passe d&apos;application dans les paramètres de sécurité de ton compte.
                            </p>
                        )}
                    </div>
                    {isDivers && (
                        <ImapFields
                            item={item}
                            state={imap}
                            setState={setImap}
                            onTestConnection={testConnection}
                            testing={testing}
                        />
                    )}
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => { setOpen(false); setPassword(""); }} size="sm">
                            Annuler
                        </Button>
                        <Button onClick={save} disabled={saving} size="sm" className="bg-primary hover:bg-primary/90"
                                data-testid={`provider-${item.provider}-save`}>
                            {saving ? "Enregistrement…" : "Enregistrer"}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function ProviderCredentialsCard() {
    const { data, mutate } = useSWR("/credentials", fetcher);
    const items = (data?.items || []).filter((it) => PROVIDER_META[it.provider]);

    return (
        <Card className="ec-card" data-testid="provider-credentials-card">
            <CardContent className="p-6 space-y-4">
                <div>
                    <h3 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
                        <Lock size={18} weight="duotone" />
                        Identifiants des fournisseurs
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                        Stockés chiffrés (AES). Utilisés pour automatiser l&apos;authentification dans le
                        navigateur live au moment des imports.
                    </p>
                </div>

                <div className="space-y-2">
                    {items.map((it) => (
                        <ProviderRow key={it.provider} item={it} onChanged={mutate} />
                    ))}
                    {!items.length && (
                        <div className="text-sm text-muted-foreground py-6 text-center">
                            Chargement…
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
